import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import Panel from "../../../components/ui/Panel/Panel.jsx";
import Button from "../../../components/ui/Button/Button.jsx";
import OptionCard from "../../../components/forms/OptionCard/OptionCard.jsx";
import Checkbox from "../../../components/forms/Checkbox/Checkbox.jsx";
import FormField from "../../../components/forms/FormField/FormField.jsx";
import Select from "../../../components/forms/Select/Select.jsx";
import TextInput from "../../../components/forms/TextInput/TextInput.jsx";
import Pagination from "../../../components/ui/Pagination/Pagination.jsx";
import BulkImportModal from "../../../components/bulk/BulkImportModal.jsx";
import ConfirmModal from "../../../components/ui/Modal/ConfirmModal.jsx";
import BulkDeleteBar from "../../../components/ui/BulkDeleteBar/BulkDeleteBar.jsx";
import TriStateCheckbox from "../../../components/ui/TriStateCheckbox/TriStateCheckbox.jsx";
import { RATES, WEIGHTS, visibleMonthOptions } from "../../../data/rates.js";
import { useApp } from "../../../hooks/useApp.js";
import { useRowSelection } from "../../../hooks/useRowSelection.js";
import { money, round2 } from "../../../utils/format.js";
import { truckTax } from "../../../utils/tax.js";
import { isValidVin, sanitizeVinInput } from "../../../utils/validators.js";
import { parseYesNo } from "../../../utils/bulkImport.js";
import { getAmendReasons, getWeightCategories, getFirstUsedMonths, getTaxYears } from "../../../services/lookupService.js";
import { getToken } from "../../../services/api.js";
import {
  addVehicle,
  updateVehicle,
  deleteVehicle,
  deleteVehiclesBulk,
  updateReturnMonth,
  bulkAddVehicles,
} from "../../../services/trucksService.js";
import { saveFormType, updateReturnYearMonth } from "../../../services/returnService.js";

const WEIGHT_CATEGORIES = Object.keys(RATES);
const PAGE_SIZE = 10;

// Only TY2026 is offered for now - matches the current backend seed data
// (lookup_tax_years). Kept as the initial state so the field still renders
// immediately if the lookup fetch is slow or fails.
const DEFAULT_TAX_YEARS = [{ value: "2026" }];
const yearLabel = (year) => `${year}-${Number(year) + 1}`;

// The IRS allows the Amended Return box to be checked for only these two
// reasons (i2290 "Amended return" / Form 2290 checkbox text: "Don't check
// this box for any other reason"). Matches the backend seed data
// (lookup_amend_reasons) - kept as the initial state so the step still
// renders immediately if the lookup fetch is slow or fails.
const DEFAULT_AMEND_REASONS = [
  {
    value: "weight",
    title: "Taxable gross weight increased",
    sub: "A vehicle got heavier and moved into a higher category mid-year — extra tax on Line 3.",
  },
  {
    value: "mileage",
    title: "Suspended vehicle went over its mileage limit",
    sub: "A previously suspended truck passed 5,000 miles (7,500 agricultural) — the tax now becomes due.",
  },
];

// Weight increase (i2290 Line 3 TGWIncreaseWorksheet) still needs its own
// page - see WeightIncreaseStep, wired in by stepConfig.js. Mileage exceeded
// is simple enough (one month + a vehicle list) that it's folded straight
// into this page instead of a separate step, so onNext here either advances
// to WeightIncreaseStep (reason === "weight") or, for mileage, has already
// collected and saved everything needed to move on.
const AmendedReturnStep = ({ onNext, onBack }) => {
  const { state, dispatch, showToast } = useApp();
  const [amendReasons, setAmendReasons] = useState(DEFAULT_AMEND_REASONS);
  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [reasonError, setReasonError] = useState(false);

  // Mileage-exceeded fields (only used when reason === "mileage").
  const [monthError, setMonthError] = useState(false);
  const [vin, setVin] = useState("");
  const [cat, setCat] = useState("");
  const [logging, setLogging] = useState(false);
  const [vinError, setVinError] = useState(false);
  const [catError, setCatError] = useState(false);
  const [trucksError, setTrucksError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [removeError, setRemoveError] = useState("");
  const [yearSaving, setYearSaving] = useState(false);
  const [monthSaving, setMonthSaving] = useState(false);
  // editingId keys off the backend truck id (usingBackend path); editingIndex
  // keys off array position for the local/no-backend fallback, which has no
  // stable id to key off (see AppContext.jsx's UPDATE_TRUCK case).
  const [editingId, setEditingId] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [page, setPage] = useState(0);
  const [bulkOpen, setBulkOpen] = useState(false);
  const localVinsRef = useRef(null);
  const isEditing = editingId != null || editingIndex != null;

  // Checkbox multi-select + bulk delete for the mileage-exceeded vehicle
  // list - separate from the single-row pendingRemove/removeError state
  // above, so the existing one-at-a-time Delete flow is untouched.
  const selection = useRowSelection();
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkError, setBulkError] = useState("");
  // Isolated from state.trucks (Original Return) - see AppContext.jsx's
  // mileageVehicles field and SET_RETURN routing comment.
  const pageCount = Math.max(1, Math.ceil(state.mileageVehicles.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageStart = safePage * PAGE_SIZE;
  const pageTrucks = state.mileageVehicles.slice(pageStart, pageStart + PAGE_SIZE);

  // "Select all" covers every loaded vehicle, not just the current page -
  // the whole list is already loaded client-side (Requirement 15).
  const allRowIds = state.mileageVehicles.map((t, i) => t.id ?? i);
  const selectedOnScreen = allRowIds.reduce((n, id) => n + (selection.selectedIds.has(id) ? 1 : 0), 0);
  const headerChecked = allRowIds.length > 0 && selectedOnScreen === allRowIds.length;
  const headerIndeterminate = selectedOnScreen > 0 && selectedOnScreen < allRowIds.length;

  const reason = state.amendReason;
  const setReason = (value) => {
    dispatch({ type: "SET_AMEND_REASON", reason: value });
    setReasonError(false);
  };

  // The IRS allows exactly one reason per amended return ("Don't check this
  // box for any other reason") - weight-increase (amendVehicles, saved via
  // WeightIncreaseStep's saveFormType) and mileage-exceeded (mileageVehicles,
  // saved as ordinary Truck rows) are mutually exclusive datasets on the
  // return. Without a lock, picking "weight", entering data on
  // WeightIncreaseStep, then backing up here and picking "mileage" instead
  // left the weight-increase data stranded server-side under a return whose
  // reason no longer matches it - reachable again only by switching back.
  // Once either reason has real data attached, the OTHER OptionCard is
  // disabled outright (can't be clicked into at all); before any data
  // exists, re-clicking the already-selected card still clears the pick so a
  // simple mis-click doesn't get anyone stuck.
  const hasReasonData = state.amendVehicles.length > 0 || state.mileageVehicles.length > 0;

  const month = state.amendMonthMileage;
  const usingBackend = !!(getToken() && state.returnId);

  // Rejected-return-resubmission vehicle info (informational only, same pattern as
  // TrucksStep/VinCorrectionStep/WeightIncreaseStep - adding more vehicles than
  // originally filed is allowed; an additional service fee applies before
  // resubmitting, enforced server-side at submit time).
  const hasRejectionInfo = state.rejectedResubmission && state.originalVehicleCount != null;
  const extraPendingFee = state.extraVehiclesPendingFee ?? 0;

  const monthOptions = state.monthsLookup.length
    ? state.monthsLookup.map((m) => [m.value, m.label])
    : visibleMonthOptions().map((m) => [m[0], m[1]]);

  const taxYearOptions = state.taxYearsLookup.length ? state.taxYearsLookup : DEFAULT_TAX_YEARS;

  // Rate shown switches between the standard and logging PERIOD amount
  // (periodNormalAmt/periodLoggingAmt - the partial-period rate for
  // state.month, "Month first used (this return)") as the Logging checkbox
  // is toggled - same convention as TrucksStep, except keyed off the
  // period rate rather than the flat annual one, since these vehicles are
  // taxed from their own first-used month, not always a full year.
  const catOptions = state.weightCategoriesLookup.length
    ? state.weightCategoriesLookup.map((w) => [
        w.code,
        `${w.code} · ${w.weightLabel} · ${money(logging ? w.periodLoggingAmt : w.periodNormalAmt)}`,
      ])
    : WEIGHT_CATEGORIES.map((c) => [c, `${c} · ${WEIGHTS[c]} · ${money(RATES[c][logging ? 1 : 0])}/yr`]);

  const validCatCodes = useMemo(
    () => new Set(state.weightCategoriesLookup.length ? state.weightCategoriesLookup.map((w) => w.code) : WEIGHT_CATEGORIES),
    [state.weightCategoriesLookup]
  );

  // "CODE - weight range" strings for the bulk-upload template's Excel
  // dropdown - see TrucksStep's catDropdownOptions for why. parse (below)
  // strips the " - ..." part back off before saving.
  const catDropdownOptions = useMemo(
    () =>
      (state.weightCategoriesLookup.length
        ? state.weightCategoriesLookup.map((w) => [w.code, w.weightLabel])
        : WEIGHT_CATEGORIES.map((c) => [c, WEIGHTS[c]])
      )
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([code, label]) => `${code} - ${label}`),
    [state.weightCategoriesLookup]
  );

  // Bulk-upload columns for the mileage-exceeded vehicle table - these are
  // normal taxable vehicles (no suspended/agri, matching addTruck's payload
  // { vin, cat, logging, suspended: false } below).
  const bulkColumns = useMemo(
    () => [
      {
        key: "vin",
        header: "VIN",
        required: true,
        example: "1FUJGLDR9CLBP8834",
        description: "The vehicle that went over its mileage limit. Letters and numbers only.",
        parse: (v) => sanitizeVinInput(String(v ?? "")),
        validate: (v) => (isValidVin(v) ? "" : "Not a valid VIN."),
      },
      {
        key: "cat",
        header: "Weight category (A-W)",
        required: true,
        example: "D",
        description: "Single letter A-W weight category. Pick from the dropdown - it shows each code's weight range.",
        dropdown: catDropdownOptions,
        parse: (v) => String(v ?? "").trim().split(" - ")[0].trim().toUpperCase(),
        validate: (v) => {
          if (!v) return "Weight category is required.";
          if (!validCatCodes.has(v)) return "Not a recognized weight category.";
          return "";
        },
      },
      {
        key: "logging",
        header: "Logging (Yes/No)",
        required: false,
        example: "No",
        description: "Yes if this truck is used exclusively for hauling harvested forest products (lower tax rate).",
        parse: parseYesNo,
      },
    ],
    [validCatCodes, catDropdownOptions]
  );

  // Same usingBackend/local-fallback branch addTruck uses, minus the
  // per-field form state - called once per parsed row by BulkImportModal.
  // localVinsRef mirrors TrucksStep's approach: the local-fallback duplicate
  // check needs a running set (not just state.mileageVehicles) since
  // dispatches in this loop don't necessarily re-render between rows.
  const bulkAddRow = async (payload) => {
    const full = { ...payload, suspended: false };
    if (usingBackend) {
      const r = await addVehicle(state.returnId, full);
      // formTypeCd on the return isn't persisted as "amended" until this
      // step's own Continue (saveFormType, below) - a row-add before that
      // echoes back the return's still-stale formTypeCd (e.g. "original"),
      // which would otherwise overwrite state.returnType via SET_RETURN's
      // `action.returnType || state.returnType` fallback and reroute the
      // wizard out of this step (stepConfig derives the step sequence from
      // state.returnType). Same bug/fix as VinCorrectionStep. This step only
      // renders once returnType is already "amended", so pinning is correct.
      dispatch({ type: "SET_RETURN", ...r, returnType: "amended", amendReason: "mileage" });
      syncMileageFormType(month);
      return r;
    }
    if (!localVinsRef.current) localVinsRef.current = new Set(state.mileageVehicles.map((t) => t.vin));
    if (localVinsRef.current.has(full.vin)) {
      throw new Error("That VIN is already on this return.");
    }
    localVinsRef.current.add(full.vin);
    dispatch({ type: "ADD_MILEAGE_VEHICLE", truck: full });
    return full;
  };

  // Real bulk endpoint (one request for every row) - used instead of
  // bulkAddRow's per-row loop whenever a backend return is loaded.
  const bulkAdd = async (rows) => {
    const r = await bulkAddVehicles(state.returnId, rows.map((row) => ({ ...row, suspended: false })));
    // See bulkAddRow above: pin returnType against the stale pre-Continue echo.
    dispatch({ type: "SET_RETURN", ...r, returnType: "amended", amendReason: "mileage" });
    syncMileageFormType(month);
    return r.results;
  };

  useEffect(() => {
    let cancelled = false;
    Promise.all([getAmendReasons(), getFirstUsedMonths(), getTaxYears()])
      .then(([reasons, months, taxYears]) => {
        if (cancelled) return;
        if (Array.isArray(reasons) && reasons.length) {
          setAmendReasons(reasons.map((r) => ({ value: r.code, title: r.title, sub: r.sub })));
        }
        if (Array.isArray(months) && months.length) dispatch({ type: "SET_LOOKUPS", months });
        if (Array.isArray(taxYears) && taxYears.length) {
          dispatch({ type: "SET_LOOKUPS", taxYears: taxYears.map((y) => ({ value: y.code })) });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLookupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Weight categories carry a per-month partial-period rate - refetch
  // whenever "Month first used (this return)" changes so the Weight
  // category dropdown always reflects that month's rate, not just July's.
  useEffect(() => {
    if (reason !== "mileage") return;
    let cancelled = false;
    getWeightCategories(state.month)
      .then((weightCategories) => {
        if (!cancelled && Array.isArray(weightCategories) && weightCategories.length) {
          dispatch({ type: "SET_LOOKUPS", weightCategories });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.month, reason]);

  const handleYearChange = async (e) => {
    const year = e.target.value;
    if (usingBackend) {
      setYearSaving(true);
      try {
        const r = await updateReturnYearMonth(state.returnId, { taxYear: year, firstUsedMonth: state.month });
        // See bulkAddRow above: pin returnType against the stale pre-Continue echo.
        dispatch({ type: "SET_RETURN", ...r, returnType: "amended", amendReason: "mileage" });
      } catch (err) {
        showToast(err.message || "Couldn't save the tax year. Please try again.");
      } finally {
        setYearSaving(false);
      }
      return;
    }
    dispatch({ type: "SET_TAX_YEAR", year });
  };

  const handleMonthChange = async (e) => {
    const monthVal = e.target.value;
    if (usingBackend) {
      setMonthSaving(true);
      try {
        const r = await updateReturnMonth(state.returnId, monthVal);
        // See bulkAddRow above: pin returnType against the stale pre-Continue echo.
        dispatch({ type: "SET_RETURN", ...r, returnType: "amended", amendReason: "mileage" });
      } catch (err) {
        showToast(err.message || "Couldn't update the month. Please try again.");
      } finally {
        setMonthSaving(false);
      }
      return;
    }
    dispatch({ type: "SET_MONTH", month: monthVal });
  };

  // Best-effort: commits formTypeCd="amended"/amendReasonCd="mileage"/
  // amendMonthMileage to the backend as soon as this required field is
  // filled in - rather than waiting for this step's own Continue (see
  // handleContinue below) - and again after every vehicle add as a safety
  // net in case that first call failed. Without this, a filer who adds a
  // vehicle and leaves via My Filings before Continue resumes into a return
  // the backend still thinks is "original": the vehicle reappears under the
  // Trucks bucket instead of here, and re-adding the same VIN throws
  // "already added" (see AppContext.jsx's SET_RETURN isMileageCtx routing).
  // Silently gives up on failure - the next add, or Continue, retries with
  // a complete payload.
  const syncMileageFormType = async (monthValue) => {
    if (!usingBackend || !monthValue) return;
    try {
      const r = await saveFormType(state.returnId, {
        returnType: "amended",
        addressChange: state.addressChange,
        amendReason: "mileage",
        amendMonthMileage: monthValue,
        taxPreparerId: state.returnTaxPreparerId,
      });
      dispatch({ type: "SET_RETURN", ...r, returnType: "amended", amendReason: "mileage" });
    } catch {
      // best-effort - see comment above
    }
  };

  const handleMileageMonthChange = (e) => {
    const monthVal = e.target.value;
    dispatch({ type: "SET_AMEND_MONTH_MILEAGE", month: monthVal });
    setMonthError(false);
    syncMileageFormType(monthVal);
  };

  const resetForm = () => {
    setVin("");
    setCat("");
    setLogging(false);
    setVinError(false);
    setCatError(false);
    setEditingId(null);
    setEditingIndex(null);
  };

  // Loads an existing vehicle back into the draft form for editing - same
  // pattern as TrucksStep's startEdit, but tracks by id (backend) or index
  // (local fallback) depending on which mode this return is in.
  const startEdit = (i) => {
    const t = state.mileageVehicles[i];
    if (!t) return;
    setVin(t.vin || "");
    setCat(t.cat || "");
    setLogging(!!t.logging);
    setVinError(false);
    setCatError(false);
    if (usingBackend) {
      setEditingId(t.id);
      setEditingIndex(null);
    } else {
      setEditingIndex(i);
      setEditingId(null);
    }
  };

  const addTruck = async () => {
    // Required before any vehicle can be added - see syncMileageFormType's
    // comment: this is also the earliest point the backend can be told this
    // return is amended/mileage, so requiring it up front (instead of only
    // at Continue) keeps a vehicle from ever being added while the backend
    // still thinks this return is "original".
    if (!month) {
      setMonthError(true);
      return;
    }
    const v = vin.trim().toUpperCase();
    const vinOk = isValidVin(v);
    setVinError(!vinOk);
    if (!vinOk) return;
    if (!cat) {
      setCatError(true);
      return;
    }
    const duplicate = state.mileageVehicles.some((t, i) =>
      t.vin === v && (usingBackend ? t.id !== editingId : i !== editingIndex)
    );
    if (duplicate) {
      setVinError(true);
      return;
    }
    const payload = { vin: v, cat, logging, suspended: false };

    if (usingBackend) {
      setSaving(true);
      try {
        const r = editingId != null ? await updateVehicle(editingId, payload) : await addVehicle(state.returnId, payload);
        // See bulkAddRow above: pin returnType against the stale pre-Continue echo.
        dispatch({ type: "SET_RETURN", ...r, returnType: "amended", amendReason: "mileage" });
        syncMileageFormType(month);
      } catch (err) {
        showToast(err.message || "Couldn't save the vehicle. Please try again.");
        setSaving(false);
        return;
      }
      setSaving(false);
    } else if (editingIndex != null) {
      dispatch({ type: "UPDATE_MILEAGE_VEHICLE", index: editingIndex, patch: payload });
    } else {
      dispatch({ type: "ADD_MILEAGE_VEHICLE", truck: payload });
    }

    resetForm();
    setTrucksError(false);
  };

  const removeTruck = (i) => {
    const t = state.mileageVehicles[i];
    if (usingBackend && t?.id) {
      setRemoveError("");
      setPendingRemove(t);
      return;
    }
    dispatch({ type: "REMOVE_MILEAGE_VEHICLE", index: i });
    if (editingIndex === i) {
      resetForm();
    } else if (editingIndex != null && i < editingIndex) {
      setEditingIndex((idx) => (idx != null ? idx - 1 : idx));
    }
  };

  const cancelRemoveTruck = () => {
    setPendingRemove(null);
    setRemoveError("");
  };

  const confirmRemoveTruck = async () => {
    const t = pendingRemove;
    if (!t) return;
    setDeletingId(t.id);
    try {
      const r = await deleteVehicle(t.id);
      // See bulkAddRow above: pin returnType against the stale pre-Continue echo.
      dispatch({ type: "SET_RETURN", ...r, returnType: "amended", amendReason: "mileage" });
      if (editingId === t.id) resetForm();
      setPendingRemove(null);
      setRemoveError("");
    } catch (err) {
      setRemoveError(err.message || "Couldn't remove the vehicle. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  // Bulk delete - selection.selectedIds holds real backend ids when
  // usingBackend (mileage vehicles are Truck rows, same as Trucks step - see
  // deleteVehiclesBulk) or array indices in the local/no-backend fallback,
  // same split as removeTruck/confirmRemoveTruck above. Always confirms
  // first regardless of mode, unlike single-row delete.
  const openBulkDelete = () => {
    setBulkError("");
    setBulkConfirmOpen(true);
  };

  const cancelBulkDelete = () => {
    setBulkConfirmOpen(false);
    setBulkError("");
  };

  const confirmBulkDelete = async () => {
    const count = selection.count;
    setBulkDeleting(true);
    try {
      if (usingBackend) {
        const r = await deleteVehiclesBulk(state.returnId, Array.from(selection.selectedIds));
        dispatch({ type: "SET_RETURN", ...r, returnType: "amended", amendReason: "mileage" });
      } else {
        dispatch({ type: "BULK_REMOVE_MILEAGE_VEHICLES", indices: selection.selectedIds });
      }
      // Reset the edit form if the row being edited was just deleted -
      // editingId (backend mode, ids) and editingIndex (local mode, indices)
      // are mutually exclusive, matching startEdit's own split.
      if (
        (editingId != null && selection.selectedIds.has(editingId)) ||
        (editingIndex != null && selection.selectedIds.has(editingIndex))
      ) {
        resetForm();
      }
      selection.clear();
      setBulkConfirmOpen(false);
      setBulkError("");
      showToast(`${count} vehicle${count === 1 ? "" : "s"} deleted successfully.`);
    } catch (err) {
      setBulkError(err.message || "Unable to delete the selected vehicles. Please try again.");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleContinue = async () => {
    if (!reason) {
      setReasonError(true);
      return;
    }

    if (reason !== "mileage") {
      onNext();
      return;
    }

    if (!month) {
      setMonthError(true);
      return;
    }
    if (!state.mileageVehicles.length) {
      setTrucksError(true);
      return;
    }

    if (usingBackend) {
      setSaving(true);
      try {
        const r = await saveFormType(state.returnId, {
          returnType: "amended",
          addressChange: state.addressChange,
          amendReason: "mileage",
          amendMonthMileage: month,
          taxPreparerId: state.returnTaxPreparerId,
        });
        dispatch({ type: "SET_RETURN", ...r });
      } catch (err) {
        setSaving(false);
        showToast(err.message || "Couldn't save. Please try again.");
        return;
      }
      setSaving(false);
    }

    onNext();
  };

  return (
    <Panel padded>
      <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
        Amended return
      </div>
      <h2 className="text-2xl m-[4px_0]">Why are you amending?</h2>
      <p className="text-[var(--muted)] max-w-[62ch] mb-4">
        The IRS allows an amended return for only these two reasons.
      </p>

      <div className="grid gap-3">
        <div className="font-semibold text-sm mb-1">
          Reason <span className="text-[var(--danger)]">*</span>
        </div>
        {amendReasons.map((o) => {
          const isSelected = reason === o.value;
          const otherDisabled = !!reason && !isSelected;
          return (
            <OptionCard
              key={o.value}
              title={o.title}
              subtitle={o.sub}
              selected={isSelected}
              onClick={() => {
                if (isSelected) {
                  if (!hasReasonData) setReason("");
                  return;
                }
                setReason(o.value);
              }}
              disabled={lookupsLoading || otherDisabled}
            />
          );
        })}
        {reasonError && <div className="text-xs text-[var(--danger)]">Pick the reason for this amended return.</div>}
        {/* {reason && (
          <div className="text-xs text-[var(--muted)]">
            {!hasReasonData
              ? "Click your selection again to pick a different reason."
              : reason === "mileage"
              ? "Remove the vehicle(s) below to pick a different reason."
              : "Remove the vehicle(s) on the next step to pick a different reason."}
          </div>
        )} */}
      </div>

      {reason === "mileage" && (
        <>
          <FormField label="Tax year" hint={yearSaving ? "Saving…" : undefined} style={{ maxWidth: 300 }}>
            <Select value={state.year} onChange={handleYearChange} disabled={lookupsLoading || yearSaving}>
              {taxYearOptions.map((y) => (
                <option key={y.value} value={y.value}>
                  {yearLabel(y.value)}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField
            label="Month first used (this return)"
            hint={monthSaving ? "Saving…" : "Sets the partial-period rate shown in the Weight category dropdown below."}
            style={{ maxWidth: 300 }}
          >
            <Select value={state.month} onChange={handleMonthChange} disabled={lookupsLoading || monthSaving}>
              {monthOptions.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField
            label="Month the mileage limit was exceeded"
            required
            hint="Written next to the Amended Return box — one month per amended return."
            error="Pick the month the mileage limit was exceeded."
            showError={monthError}
            style={{ maxWidth: 300 }}
          >
            <Select
              value={month}
              onChange={handleMileageMonthChange}
              invalid={monthError}
              disabled={lookupsLoading}
            >
              <option value="">Select month…</option>
              {monthOptions.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FormField>

          <h3 className="text-[15px] font-semibold mt-4 mb-1">
            Vehicle(s) that went over the limit <span className="text-[var(--danger)]">*</span>
          </h3>
          <p className="text-[var(--muted)] text-sm mb-2.5">
            Add them as normal taxable vehicles. The tax is figured from each vehicle's first-use month and reported
            on Line 2.
          </p>

          {hasRejectionInfo && (
            <div
              className="rounded-[var(--r-sm)] border p-3 mb-4 text-[13px]"
              style={
                extraPendingFee > 0
                  ? { borderColor: "var(--amber)", background: "var(--amber-soft)", color: "var(--amber)" }
                  : { borderColor: "var(--line-2)", background: "var(--panel-2)", color: "var(--muted)" }
              }
            >
              <b className="font-semibold" style={{ color: extraPendingFee > 0 ? "var(--amber)" : "var(--ink)" }}>
                Vehicles: {state.mileageVehicles.length} (originally filed: {state.originalVehicleCount})
              </b>
              <div className="mt-0.5">
                {extraPendingFee > 0
                  ? `You've added ${extraPendingFee} vehicle${extraPendingFee === 1 ? "" : "s"} beyond what was originally filed — an additional service fee applies before this return can be resubmitted (you'll pay it on the Payment Details step).`
                  : `This return was rejected by the IRS. You can add, remove, or correct vehicles freely — adding more than the ${state.originalVehicleCount} originally filed will require an additional service fee before resubmitting.`}
              </div>
            </div>
          )}

          <Panel tone="soft" className="!shadow-none p-[18px] mb-3">
            <div className="flex items-center justify-between mb-3.5">
              <h3 className="text-[19px]">
                {isEditing ? "Update this vehicle" : "Add a vehicle"}
              </h3>
              {!isEditing && (
                <Button
                  variant="ghost"
                  onClick={() => (month ? setBulkOpen(true) : setMonthError(true))}
                  disabled={saving}
                >
                  ⇪ Bulk upload
                </Button>
              )}
            </div>
            <div className="grid gap-3.5 sm:grid-cols-2">
              <FormField label="VIN" required error="Enter a valid VIN." showError={vinError}>
                <TextInput
                  mono
                  maxLength={19}
                  placeholder="Vehicle that went over the limit"
                  value={vin}
                  onChange={(e) => setVin(sanitizeVinInput(e.target.value))}
                  invalid={vinError}
                  disabled={lookupsLoading || saving}
                />
              </FormField>
              <FormField label="Weight category" required error="Pick a category." showError={catError}>
                <Select
                  value={cat}
                  onChange={(e) => {
                    setCat(e.target.value);
                    setCatError(false);
                  }}
                  invalid={catError}
                  disabled={lookupsLoading || saving}
                >
                  <option value="">Category…</option>
                  {catOptions.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </FormField>
            </div>
            <Checkbox
              id="mileageLog"
              title="Logging truck"
              subtitle="Applies the lower logging rate."
              checked={logging}
              onChange={(e) => setLogging(e.target.checked)}
              disabled={saving}
            />
            <div className="flex gap-2">
              <Button variant="ghost" onClick={addTruck} disabled={saving}>
                {saving ? "Saving…" : isEditing ? "Update vehicle" : "+ Add vehicle"}
              </Button>
              {isEditing && (
                <Button variant="ghost" onClick={resetForm} disabled={saving}>
                  Cancel
                </Button>
              )}
            </div>
          </Panel>

          <BulkDeleteBar count={selection.count} onDelete={openBulkDelete} loading={bulkDeleting} />
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="text-left p-3 border-b border-[var(--line)] w-[36px]">
                  <TriStateCheckbox
                    checked={headerChecked}
                    indeterminate={headerIndeterminate}
                    onChange={() => selection.selectAll(headerChecked ? [] : allRowIds)}
                    label="Select all vehicles"
                  />
                </th>
                {["#", "VIN", "Category", "Type", "Tax", "Actions"].map((h, hi) => (
                  <th
                    key={h}
                    className={[
                      "text-left p-3 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold",
                      hi === 4 ? "text-right" : "",
                    ].join(" ")}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {state.mileageVehicles.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-[var(--muted)] p-[22px_12px]">
                    No vehicles yet. Add your first one above.
                  </td>
                </tr>
              ) : (
                pageTrucks.map((t, pageIndex) => {
                  const i = pageStart + pageIndex;
                  const rowId = t.id ?? i;
                  return (
                  <tr key={t.id ?? `${t.vin}-${i}`} className="hover:bg-[var(--accent-soft)] transition-colors">
                    <td className="text-left p-3 border-b border-[var(--line)]">
                      <TriStateCheckbox
                        checked={selection.selectedIds.has(rowId)}
                        onChange={() => selection.toggle(rowId)}
                        label={`Select row ${i + 1}`}
                      />
                    </td>
                    <td className="text-left p-3 border-b border-[var(--line)]">{i + 1}</td>
                    <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">{t.vin}</td>
                    <td className="text-left p-3 border-b border-[var(--line)]">
                      {t.cat} · {WEIGHTS[t.cat] || ""}
                    </td>
                    <td className="text-left p-3 border-b border-[var(--line)]">{t.logging ? "Logging" : "Standard"}</td>
                    <td className="text-right p-3 border-b border-[var(--line)] font-mono text-[13px]">
                      {money(t.taxAmt != null ? Number(t.taxAmt) : truckTax(t, state.month))}
                    </td>
                    <td className=" p-3 border-b border-[var(--line)] whitespace-nowrap">
                      <button
                        type="button"
                        title="Edit"
                        onClick={() => startEdit(i)}
                        className="inline-flex items-center justify-center border-0 bg-transparent text-[var(--dim)] cursor-pointer p-1 hover:text-[var(--accent)]"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        title="Delete"
                        onClick={() => removeTruck(i)}
                        disabled={deletingId === t.id}
                        className="inline-flex items-center justify-center border-0 bg-transparent text-[var(--dim)] cursor-pointer p-1 ml-1 hover:text-[var(--danger)] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <Pagination page={safePage} pageCount={pageCount} onChange={setPage} />
          {trucksError && <div className="text-xs text-[var(--danger)] mb-3 mt-3">Add at least one vehicle.</div>}

          {/* i2290 "Suspended vehicles exceeding the mileage use limit": "Report
              the tax on Form 2290, line 2" - NOT line 3 (that's exclusively the
              taxable-gross-weight-increase worksheet, see WeightIncreaseStep).
              These vehicles are ordinary taxable vehicles, so this total is
              exactly what Review's Tax summary panel shows as Line 2. */}
          <div className="flex justify-between items-baseline mt-3.5 pt-3 border-t-2 border-[var(--ink)]">
            <span className="font-disp uppercase tracking-[.08em] text-[13px]">Tax on these vehicles (line 2)</span>
            <b className="font-mono text-2xl font-semibold">
              {money(
                round2(
                  state.mileageVehicles.reduce(
                    (sum, t) => sum + (t.taxAmt != null ? Number(t.taxAmt) : truckTax(t, state.month)),
                    0
                  )
                )
              )}
            </b>
          </div>

          <BulkImportModal
            open={bulkOpen}
            onClose={() => {
              setBulkOpen(false);
              localVinsRef.current = null;
            }}
            title="Bulk upload vehicles"
            columns={bulkColumns}
            filename="form2290-amended-vehicles-template.xlsx"
            sheetName="Vehicles"
            addRow={bulkAddRow}
            bulkAdd={usingBackend ? bulkAdd : undefined}
            templateId="form2290-mileage-exceeded-v1"
          />
        </>
      )}

      <div className="flex justify-between gap-3 mt-6">
        <Button variant="ghost" onClick={onBack}>
          ← Back
        </Button>
        <Button onClick={handleContinue} disabled={saving}>
          {saving ? "Saving…" : "Continue →"}
        </Button>
      </div>

      <ConfirmModal
        open={!!pendingRemove}
        title="Delete Confirmation"
        confirmLabel="Delete"
        loading={deletingId === pendingRemove?.id}
        error={removeError}
        onConfirm={confirmRemoveTruck}
        onCancel={cancelRemoveTruck}
      >
        Are you sure you want to delete this record? This action cannot be undone.
      </ConfirmModal>

      <ConfirmModal
        open={bulkConfirmOpen}
        title="Delete Selected Records?"
        confirmLabel="Delete"
        loading={bulkDeleting}
        error={bulkError}
        onConfirm={confirmBulkDelete}
        onCancel={cancelBulkDelete}
      >
        Are you sure you want to delete these {selection.count} record{selection.count === 1 ? "" : "s"}? This action
        cannot be undone.
      </ConfirmModal>
    </Panel>
  );
};

export default AmendedReturnStep;
